import { FeatureExposureType } from '@/components/intro-wizards/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import featureExposureService from '../services/feature-exposure.service';

export enum FeatureExposureStorageKey {
  ActionPlan = 'hasActionPlanIntroWizardBeenShown',
  TestingPlan = 'hasTestingPlanIntroWizardBeenShown',
}

/**
 * Hook to check if a feature has been seen by the user
 * @param type The type of feature exposure to check
 * @param localStorageKey The localStorage key for backward compatibility
 */
export function useFeatureExposureCheck(type: FeatureExposureType, localStorageKey: FeatureExposureStorageKey) {
  return useQuery({
    queryKey: ['featureExposure', type],
    queryFn: async () => {
      // First check the backend
      const hasBeenSeenInBackend = await featureExposureService.hasFeatureBeenSeen(type);
      if (hasBeenSeenInBackend) {
        return true;
      }

      // Fall back to localStorage for backward compatibility
      const hasBeenSeenInLocalStorage = Boolean(localStorage.getItem(localStorageKey));

      // If it was seen in localStorage but not in backend, update the backend
      if (hasBeenSeenInLocalStorage) {
        await featureExposureService.markFeatureAsSeen(type);
      }

      return hasBeenSeenInLocalStorage;
    },
  });
}

/**
 * Hook to mark a feature as seen by the user
 * @param type The type of feature exposure to mark
 */
export function useFeatureExposureMark(type: FeatureExposureType) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => featureExposureService.markFeatureAsSeen(type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['featureExposure', type] });
    },
  });
}
